import { permissions } from '@app/permissions/permissions';
import usePermissions from '@app/permissions/usePermissions';
import { IdName } from '@lib/utils/id-name';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import useInboxState from '@modules/InboxModule/hooks/useInboxState';
import { inboxActions } from '@modules/InboxModule/state';
import { BodyText, Stack } from '@servicetitan/design-system';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { DIRECT_LINE } from 'src/constants/tenantSelection';
import { useOutboundCallModalContext } from '../../../context';
import { TenantSelect } from './TenantSelect/TenantSelect';
import { AgentCallerId } from '@components/OutboundCallerIdSelection/AgentCallerId';
import { AgentCallerIdDetails } from '@components/OutboundCallerIdSelection/AgentCallerId/types';
import { OutboundCallEntity } from '@lib/types/common-call-types';

const DIRECT_LINE_TENANT_OPTION = { id: DIRECT_LINE, name: 'Direct Line' };

export const TenantSelectionSection: FC = () => {
  const { user } = useAuth();
  const { setCallFromDetails } = useOutboundCallModalContext();
  const { selectedTenantForOutboundCall: selectedTenant } = useInboxState();
  const hasPermissionToUseDID = usePermissions(
    [permissions.general.ACTION_CALL_OPTIONS.OUTBOUND_CALLS.DID_CREATE],
    'all',
  );
  const hasPersonalLine =
    hasPermissionToUseDID && user?.personalLine?.phoneNumber;

  const tenantOptions: IdName[] = useMemo(() => {
    const options =
      user?.org.tenants.map((tenant) => ({
        id: tenant.id,
        name: tenant.companyName || tenant.name,
      })) ?? [];

    if (hasPersonalLine) {
      options.unshift(DIRECT_LINE_TENANT_OPTION);
    }
    return options;
  }, [user?.org.tenants, hasPersonalLine]);

  const onTenantSelect = useCallback((tenant: IdName | null) => {
    inboxActions.setTenantForOutboundCall(tenant);
  }, []);

  useEffect(() => {
    if (!selectedTenant && tenantOptions.length > 0) {
      onTenantSelect(tenantOptions[0]);
    }
  }, [tenantOptions, onTenantSelect, selectedTenant]);

  const onChangeNumber = useCallback(
    ({ callerId, callerName }: AgentCallerIdDetails) => {
      setCallFromDetails({
        number: callerId,
        callDisplayName: callerName ?? '',
        entity: OutboundCallEntity.Keypad,
      });
    },
    [setCallFromDetails],
  );

  const onLoadingChange = useCallback(
    (loading: boolean) => {
      setCallFromDetails((prev) => ({ ...prev, loading }));
    },
    [setCallFromDetails],
  );

  return (
    <Stack direction='row' justifyContent='space-between' spacing={1}>
      <Stack direction='column' className='w-50'>
        <BodyText
          style={{ fontWeight: '600', marginBottom: '8px' }}
          size='small'
        >
          Calling From
        </BodyText>

        {tenantOptions.length > 1 ? (
          <TenantSelect
            selectedTenant={selectedTenant}
            onTenantSelect={onTenantSelect}
            tenantOptions={tenantOptions}
            defaultOption={tenantOptions[0]}
          />
        ) : (
          tenantOptions[0]?.name && <BodyText>{tenantOptions[0].name}</BodyText>
        )}
      </Stack>
      <Stack
        direction='column'
        alignItems='flex-end'
        justifyContent='space-between'
      >
        <BodyText
          style={{ fontWeight: '600', marginBottom: '5px' }}
          alignItems='flex-end'
          size='small'
        >
          Your Caller ID Displays As
        </BodyText>
        <AgentCallerId
          onChange={onChangeNumber}
          tenantId={selectedTenant?.id}
          tenantName={selectedTenant?.name}
          onLoadingChange={onLoadingChange}
        />
      </Stack>
    </Stack>
  );
};
